import { reactEvents } from '../../../../../bridge/reactEvents';

// @ngInject
const ArticleDialogService = (
  $mdDialog,
  $rootScope,
  $q,
  OrganizationsApiService,
  CustomersApiService,
  ProductGroupsApiService,
  ContactsApiService,
  ArticlesApiService,
) => ({
  showDialog: ({
    customerId = null,
    conceptId = null,
    organization = $rootScope.currentUser.organization || null,
    article = null,
  } = {}) =>
    $mdDialog.show({
      clickOutsideToClose: true,
      controller: 'ArticleDialogController as vm',
      templateUrl: 'services/dialogs/articleDialog/articleDialog.tpl.html',
      locals: {
        customerId,
        conceptId,
        organization,
      },
      resolve: {
        article: () =>
          article
            ? ArticlesApiService.getById(article.id, {
                includeRelations: [
                  'owner',
                  'matrixNodeX',
                  'matrixNodeY',
                  'productGroup',
                ],
              })
            : $q.resolve(null),
        articleTemplates: () =>
          article
            ? $q
                .all([
                  OrganizationsApiService.loadMatrixNodes(organization),
                  OrganizationsApiService.loadMatrixGroups(organization),
                ])
                .then(
                  () => [],
                  (err) => $q.reject(err),
                )
            : $q
                .all([
                  OrganizationsApiService.loadMatrixNodes(organization),
                  OrganizationsApiService.loadMatrixGroups(organization),
                  OrganizationsApiService.loadArticleTemplates(organization),
                ])
                .then(
                  (res) => res[2],
                  (err) => $q.reject(err),
                ),
        customers: () =>
          article
            ? $q.resolve([])
            : CustomersApiService.getAllByOrganizationId(organization.id, true),
        productGroups: () =>
          ProductGroupsApiService.getAllByOrganizationId(organization.id),
        contacts: () =>
          customerId
            ? ContactsApiService.getAllByCustomerId(customerId)
            : $q.resolve([]),
      },
    }),
});

class ArticleDialogController {
  constructor(
    $filter,
    $mdDialog,
    $scope,
    ArticlesApiService,
    ConceptsApiService,
    ContactsApiService,
    matrixNodeService,
    CustomersApiService,
    GarpApiService,
    toasterService,
    articleTemplates,
    organization,
    customerId,
    conceptId,
    article,
    customers,
    productGroups,
    contacts,
  ) {
    'ngInject';

    this.$filter = $filter;
    this.$mdDialog = $mdDialog;
    this.$scope = $scope;
    this.ArticlesApiService = ArticlesApiService;
    this.ConceptsApiService = ConceptsApiService;
    this.ContactsApiService = ContactsApiService;
    this.GarpApiService = GarpApiService;
    this.toasterService = toasterService;
    this.matrixNodeService = matrixNodeService;
    this.matrixGroups = organization.matrixGroups;
    this.originalArticle = article;
    this.customers = customers;
    this.productGroups = productGroups;
    this.contacts = contacts;

    this.validationErrors = [];
    this.article = {};
    this.selectedCustomer = null;
    this.selectedConcept = null;
    this.selectedContact = null;
    this.selectedMasterBranch = null;
    this.selectedProductGroup = null;
    this.articleTemplates = [];
    this.branches = [];
    this.concepts = [];
    this.articleTemplate = {};
    this.customXValueNames = {};
    this.customYValueNames = {};
    this.nodeXValues = [];
    this.nodeYValues = [];
    this.displayTextField = 'itemCode';
    this.maximumYNodeValues = 62;
    this.maximumXNodeValues = 30;
    this.canAddNodeXValues = true;
    this.canAddNodeYValues = true;

    this.matrixAxis = [
      { id: 0, name: 'Columns' },
      { id: 1, name: 'Rows' },
    ];

    if (article) {
      this.article.id = article.id;
      this.article.name = article.name;
      this.article.colorAxis = article.colorAxis;
      this.article.hasAnyPa = article.hasAnyPa;
      if (article.matrixNodeX && article.matrixNodeY) {
        this.articleTemplate = {
          matrixNodeX: article.matrixNodeX,
          matrixNodeXId: article.matrixNodeX.id,
          matrixNodeY: article.matrixNodeY,
          matrixNodeYId: article.matrixNodeY.id,
        };
      }
      this.newArticle = false;
    } else {
      this.newArticle = true;
      this.articleTemplates = _.filter(articleTemplates, { archived: false });
      if (this.articleTemplates.length > 0) {
        this.articleTemplate = this.articleTemplates[0];
        this.article.articleTemplateId = this.articleTemplate.id;
      }
      if (customerId) {
        this.article.customerId = customerId;
        $mdDialog.startLoadingSpinner();
        CustomersApiService.getById(customerId, true)
          .then((response) => {
            this.selectedCustomer = response;
            this.loadConcepts(response.id);
          })
          .finally(() => $mdDialog.stopLoadingSpinner());
      }
      if (conceptId) {
        this.article.conceptId = conceptId;
        $mdDialog.startLoadingSpinner();
        ConceptsApiService.getById(conceptId, true)
          .then((response) => (this.selectedConcept = response))
          .finally(() => $mdDialog.stopLoadingSpinner());
      }
    }
    this.branches = article ? [] : _.clone(organization.branches);

    this.resetNodeValues();
    if (article) {
      const mapArticleValue = (value) => ({
        id: value.id,
        identifier: value.matrixNodeValue.identifier,
        name: value.name || value.matrixNodeValue.name,
      });
      this.article.nodeXValues = _.map(
        article.nodeXValues || [],
        mapArticleValue,
      );
      this.article.nodeYValues = _.map(
        article.nodeYValues || [],
        mapArticleValue,
      );
    } else {
      this.article.branches = [];
    }

    this.customerId = customerId;
    this.conceptId = conceptId;
    this.article.variable = false;

    this.eventUnsub = reactEvents.newContactAddedToBrand.sub(() => {
      if (this.selectedCustomer) {
        this.loadContacts(this.selectedCustomer.id);
      }
    });

    this.eventUnsub2 = reactEvents.variantFinalized.sub(() => {
      if (this.article) this.loadArticle();
    });

    this.$scope.$on('$destroy', () => {
      this.eventUnsub();
      this.eventUnsub2();
    });
  }

  setProductGroup(productGroup) {
    this.article.productGroupId = productGroup ? productGroup.id : 0;
    this.article.productGroup = productGroup;
  }

  getCurrentProductGroups() {
    const productGroups = _.filter(this.productGroups, { archived: false });
    return productGroups;
  }

  filterProductGroups(query) {
    return _.orderBy(
      this.$filter('filter')(this.getCurrentProductGroups(), { value: query }),
      ['code'],
    );
  }

  setContact(contact) {
    this.article.contactId = contact ? contact.id : 0;
    this.article.contact = contact;
  }

  getCurrentContact() {
    const contacts = _.filter(this.contacts, { archived: false });
    return contacts;
  }

  filterContacts(query) {
    return _.orderBy(
      this.$filter('filter')(this.getCurrentContact(), { name: query }),
      ['name'],
    );
  }

  onItemCodeChange() {
    if (this.showImport) {
      this.showImport = false;
    }
  }

  onCustomerSelect(customer) {
    this.concepts = [];
    if (customer) {
      this.loadConcepts(customer.id);
      this.loadContacts(customer.id);
    }
    this.selectedConcept = null;
    this.selectedContact = null;
    this.conceptSearchText = '';
    this.contactsSearchText = '';
  }

  onConceptSelect(concept) {
    if (concept) {
      this.article.conceptId = concept.id;
    } else if (!this.newArticle) {
      this.article.conceptId = 0;
    } else {
      delete this.article.conceptId;
    }
  }

  loadConcepts(customerId) {
    this.$mdDialog.startLoadingSpinner();
    this.ConceptsApiService.getAllByCustomerId(customerId)
      .then((response) => (this.concepts = response))
      .finally(() => this.$mdDialog.stopLoadingSpinner());
  }

  loadContacts(customerId) {
    this.$mdDialog.startLoadingSpinner();
    this.ContactsApiService.getAllByCustomerId(customerId)
      .then((response) => (this.contacts = response))
      .finally(() => this.$mdDialog.stopLoadingSpinner());
  }

  loadArticle() {
    this.ArticlesApiService.getById(this.article.id).then((article) => {
      if (article) {
        this.article.lockedXNodeValueIds = article.lockedXNodeValueIds;
        this.article.lockedYNodeValueIds = article.lockedYNodeValueIds;
      }
    });
  }

  filterEntities(entities, query) {
    return this.$filter('filter')(entities, {
      name: query,
      archived: false,
    });
  }

  filterTemplates(query) {
    return _.orderBy(
      this.$filter('filter')(this.articleTemplates, { name: query }),
      'name',
    );
  }

  filterAvailableBranches(query) {
    return this.$filter('filter')(this.availableBranches, {
      name: query,
    });
  }

  loadValues(query, nodeValues, articleValues) {
    query = (query || '').toLowerCase();
    return this.$filter('filter')(nodeValues, (value) => {
      if (value.group) {
        return value.name.toLowerCase().indexOf(query) !== -1;
      }
      return (
        (value.name.toLowerCase().indexOf(query) !== -1 ||
          value.identifier.toLowerCase().indexOf(query) !== -1) &&
        !_.find(articleValues, { id: value.id })
      );
    });
  }

  loadXValues(query) {
    return this.loadValues(query, this.nodeXValues, this.article.nodeXValues);
  }

  loadYValues(query) {
    return this.loadValues(query, this.nodeYValues, this.article.nodeYValues);
  }

  xSelected(value) {
    this.selectedXValue = null;
    this.xValueSearchText = null;

    if (this.article.nodeXValues.length >= this.maximumXNodeValues) {
      this.canAddNodeXValues = false;
    }

    if (!this.canAddNodeXValues) {
      return;
    }

    this.addNodeValue(this.nodeXValues, this.article.nodeXValues, value);
  }

  ySelected(value) {
    this.selectedYValue = null;
    this.yValueSearchText = null;

    if (this.article.nodeYValues.length >= this.maximumYNodeValues) {
      this.canAddNodeYValues = false;
    }

    if (!this.canAddNodeYValues) {
      return;
    }

    this.addNodeValue(this.nodeYValues, this.article.nodeYValues, value);
  }

  addNodeValue(nodeValues, articleNodeValues, value) {
    if (!value) {
      return;
    }
    if (value.group) {
      for (let i = 0; i < value.matrixNodeValues.length; i++) {
        const groupValue = value.matrixNodeValues[i];
        const existing = _.find(nodeValues, {
          group: false,
          id: groupValue.id,
        });
        if (existing && !_.find(articleNodeValues, { id: existing.id })) {
          const newValue = _.extend({}, existing);
          if (groupValue.name) {
            newValue.name = groupValue.name;
          }
          articleNodeValues.push(newValue);
        }
      }
    } else if (!_.find(articleNodeValues, { id: value.id })) {
      articleNodeValues.push(_.extend({}, value));
    }
  }

  removeXValue(value) {
    _.remove(this.article.nodeXValues, value);
    this.canAddNodeXValues = true;
  }

  lockedXValue(value) {
    if (!this.originalArticle) {
      return false;
    }
    const lockedValue = this.originalArticle.lockedXNodeValueIds?.find(
      (id) => id === value.id,
    );
    return !!lockedValue;
  }

  removeYValue(value) {
    _.remove(this.article.nodeYValues, value);
    this.canAddNodeYValues = true;
  }

  lockedYValue(value) {
    if (!this.originalArticle) {
      return false;
    }
    const lockedValue = this.originalArticle.lockedYNodeValueIds?.find(
      (id) => id === value.id,
    );

    return !!lockedValue;
  }

  changeTemplate(template) {
    if (!this.newArticle) {
      return;
    }

    if (template) {
      this.article.articleTemplateId = this.articleTemplate.id;
      this.resetNodeValues();

      if (
        !this.article.itemCode ||
        this.article.itemCode.length > this.articleTemplate.itemCodeMaxLength
      ) {
        this.article.itemCode = '';
      }
    } else {
      this.article.articleTemplateId = 0;
      this.article.articleTemplate = null;
    }
  }

  resetNodeValues() {
    const mapGroup = (group) => ({
      group: true,
      name: group.name,
      matrixNodeValues: group.matrixNodeValues,
    });

    const mapValue = (value) => ({
      group: false,
      id: value.id,
      identifier: value.identifier,
      name: value.name,
    });

    const xGroups = this.articleTemplate.matrixNodeXId
      ? _.map(
          _.filter(this.matrixGroups, {
            matrixNodeId: this.articleTemplate.matrixNodeXId,
          }),
          mapGroup,
        )
      : [];
    const yGroups = this.articleTemplate.matrixNodeYId
      ? _.map(
          _.filter(this.matrixGroups, {
            matrixNodeId: this.articleTemplate.matrixNodeYId,
          }),
          mapGroup,
        )
      : [];

    this.nodeXValues = xGroups.concat(
      _.map(
        _.filter(
          _.get(this.articleTemplate, 'matrixNodeX.matrixNodeValues', []),
          { archived: false },
        ),
        mapValue,
      ),
    );
    this.nodeYValues = yGroups.concat(
      _.map(
        _.filter(
          _.get(this.articleTemplate, 'matrixNodeY.matrixNodeValues', []),
          { archived: false },
        ),
        mapValue,
      ),
    );
    this.article.nodeXValues = [];
    this.article.nodeYValues = [];
  }

  validateInput(nodeValues, value) {
    this.matrixNodeService.validateInput(nodeValues, value, true);
  }

  keyDown($event, nodeValues, value) {
    this.matrixNodeService.keyDown($event, nodeValues, value);
  }

  save(force) {
    this.validationErrors = [];
    this.$mdDialog.startLoadingSpinner();
    const articleCopy = _.extend({}, this.article);
    articleCopy.variable = this.article.variable;
    if (force) {
      articleCopy.force = true;
    }
    if (
      !!_.get(this.articleTemplate, 'matrixNodeX.matrixNodeValues') &&
      !!_.get(this.articleTemplate, 'matrixNodeY.matrixNodeValues')
    ) {
      articleCopy.nodeXValues = _.map(this.article.nodeXValues, (value) => {
        const node = { id: value.id };
        const originalValue = _.find(this.nodeXValues, {
          group: false,
          id: node.id,
        });
        if (originalValue && value.name !== originalValue.name) {
          node.name = value.name;
        }
        return node;
      });
      articleCopy.nodeYValues = _.map(this.article.nodeYValues, (value) => {
        const node = { id: value.id };
        const originalValue = _.find(this.nodeYValues, {
          group: false,
          id: node.id,
        });
        if (originalValue && value.name !== originalValue.name) {
          node.name = value.name;
        }
        return node;
      });
    } else {
      delete articleCopy.nodeXValues;
      delete articleCopy.nodeYValues;
    }
    if (this.newArticle) {
      if (!this.selectedCustomer) {
        this.validationErrors.push({ message: 'Please select a customer' });
        this.$mdDialog.stopLoadingSpinner();
        return;
      }
      if (this.selectedMasterBranch) {
        articleCopy.MasterBranchId = this.selectedMasterBranch.id;
      }
      if (this.selectedProductGroup) {
        articleCopy.productGroupId = this.selectedProductGroup.id;
      }
      this.ArticlesApiService.newArticle(articleCopy, this.selectedCustomer.id)
        .then(
          (article) => {
            this.toasterService.success();
            this.$mdDialog.hide(article);
          },
          (error) => {
            this.validationErrors = error.errors;
            if (error.status === 409) {
              this.showImport = true;
              return this.GarpApiService.getArticleBranches(
                this.article.itemCode,
              ).then((result) => {
                this.availableBranches = result;
              });
            }
          },
        )
        .finally(() => this.$mdDialog.stopLoadingSpinner());
    } else {
      const data = {};

      if (articleCopy.nodeXValues && articleCopy.nodeYValues) {
        data.nodeXValues = articleCopy.nodeXValues;
        data.nodeYValues = articleCopy.nodeYValues;
        data.colorAxis = articleCopy.colorAxis;
      }

      if (_.isEmpty(data)) {
        this.$mdDialog.stopLoadingSpinner();
        this.$mdDialog.hide(this.originalArticle);
        return;
      }

      this.ArticlesApiService.updateArticleMatrixNodes(data, this.article.id)
        .then(
          (article) => {
            this.toasterService.success();
            this.$mdDialog.hide(article);
          },
          (error) => (this.validationErrors = error.errors),
        )
        .finally(() => this.$mdDialog.stopLoadingSpinner());
    }
  }

  cancel() {
    this.$mdDialog.cancel();
  }

  shouldFocus(field) {
    switch (field) {
      case 'customer':
        return !this.customerId;
      case 'concept':
        return this.customerId && !this.conceptId && this.concepts.length > 0;
      case 'itemcode':
      default:
        return this.customerId;
    }
  }
}

angular
  .module('services.dialogs.articleDialog', [])
  .service('ArticleDialogService', ArticleDialogService)
  .controller('ArticleDialogController', ArticleDialogController);
